import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, { FleetPaginated, FleetPagination } from '../fetcher';

// --- Shared Types ---

export type RoboticStatus = 'Pending' | 'Finish';

export type ActivityRoboticStatus =
	| 'Waiting'
	| 'In Queueing'
	| 'Pending'
	| 'In Progress'
	| 'Finish';

export type ActivityFeedbackRowStatus = 'In Progress' | 'Success' | 'Failed';

export type PendingStep =
	| 'Customer Data'
	| 'Application Data'
	| 'Asset Data'
	| 'Financial Data'
	| 'Insurance Data'
	| 'Incentive Data';

// --- QC New Customer ---
// Moved to: @/libs/fleet-api/services/qc-customer-types.ts & qc-customer.ts

// --- QC Pra Pencairan ---
// Moved to: @/libs/fleet-api/services/qc-pra-disbursement-types.ts & qc-pra-disbursement.ts

// --- Activity Robotic Types ---

export type ActivityRoboticSearchParam = {
	pagination: FleetPagination;
	dateFrom?: string;
	dateTo?: string;
	status?: string;
	search?: { by?: string; value?: string };
};

export type ActivityRoboticListItem = {
	customerName?: string;
	frmName?: string;
	fbdhName?: string;
	disbursementNo?: string;
	purchaseOrderNo?: string;
	facilityType?: string;
	assetDescription?: string;
	lastModified?: string;
	status?: ActivityRoboticStatus;
	duration?: string;
	isActionAllowed?: boolean;
	isViewAllowed?: boolean;
};

export type ActivityFeedbackItem = {
	activityDetailId?: number;
	pendingStep?: string;
	pendingField?: string;
	pendingMessage?: string;
	startedAt?: string;
	finishedAt?: string;
	status?: ActivityFeedbackRowStatus;
	user?: string;
	notes?: string;
	isEditAllowed?: boolean;
	isRetryAllowed?: boolean;
	isViewAllowed?: boolean;
};

export type ActivityRoboticMainInfo = {
	customerId?: string;
	customerName?: string;
	frmName?: string;
	fbdhName?: string;
	sourceOrder?: string;
	facilityType?: string;
	assetDescription?: string;
	groupBmppName?: string;
	groupBmppId?: string;
	rkpDate?: string;
	resolutionFullApprovedDate?: string;
};

// status/canCancel/canReturn not confirmed in API — kept optional for fallback
export type ActivityRoboticDetail = {
	purchaseOrderNo?: string;
	status?: ActivityRoboticStatus;
	canCancel?: boolean;
	canReturn?: boolean;
	mainInfo?: ActivityRoboticMainInfo;
	histories?: ActivityFeedbackItem[];
};

export type ActivityRoboticFeedback = {
	field?: string;
	formLabel?: string;
	fieldType?: string;
	currentValue?: string;
	pendingStep?: string;
	pendingField?: string;
	pendingMessage?: string;
	pendingImage?: { id?: string; name?: string; path?: string; url?: string; format?: string };
	options?: { id?: number; value?: string }[];
};

export type ActivityRoboticFeedbackView = {
	pendingStep?: string;
	pendingField?: string;
	pendingMessage?: string;
	pendingImage?: { id?: string; name?: string; path?: string; url?: string; format?: string };
};

export type ActivitySearchOptions = {
	statusOptions?: { id?: string; value?: string }[];
	searchByOptions?: { id?: string; value?: string }[];
};

export type UpdateFeedbackPayload = {
	roboticActivityId: number;
	field: string;
	value: string;
};

// cancel/return endpoints not in API contract yet — flagged for BE
export type CancelAppIdPayload = { purchaseOrderNo: string; notes?: string };
export type ReturnToQCPayload = { purchaseOrderNo: string; notes?: string };

// --- Activity Robotic Services ---

export const searchActivityRobotic = (params: ActivityRoboticSearchParam) =>
	fetcher<FleetPaginated<ActivityRoboticListItem>>({
		api: '/robotic-activities/search',
		body: params,
	});

export const useActivityRoboticSearch = (param: ActivityRoboticSearchParam) =>
	useQuery({
		queryKey: ['searchActivityRobotic', param] as const,
		queryFn: () => searchActivityRobotic(param),
	});

export const getActivitySearchOptions = () =>
	fetcher<ActivitySearchOptions>({
		api: '/robotic-activities/get-search-options',
		body: {},
	});

export const useActivitySearchOptions = () =>
	useQuery({
		queryKey: ['getActivitySearchOptions'] as const,
		queryFn: () => getActivitySearchOptions(),
	});

export const getActivityRoboticHistories = (purchaseOrderNo: string) =>
	fetcher<ActivityRoboticDetail>({
		api: '/robotic-activities/get-histories',
		body: { purchaseOrderNo },
	});

export const useActivityRoboticDetail = (param: { purchaseOrderNo: string }) =>
	useQuery({
		queryKey: ['getActivityRoboticHistories', param] as const,
		queryFn: () => getActivityRoboticHistories(param.purchaseOrderNo),
		enabled: !!param.purchaseOrderNo,
	});

export const getActivityFeedback = (roboticActivityId: number) =>
	fetcher<ActivityRoboticFeedback>({
		api: '/robotic-activities/get-feedback',
		body: { roboticActivityId },
	});

export const useActivityFeedback = (param: { roboticActivityId: number | null }) =>
	useQuery({
		queryKey: ['getActivityFeedback', param] as const,
		queryFn: () => getActivityFeedback(param.roboticActivityId!),
		enabled: param.roboticActivityId !== null,
	});

export const getActivityFeedbackView = (roboticActivityId: number) =>
	fetcher<ActivityRoboticFeedbackView>({
		api: '/robotic-activities/get-feedback-view',
		body: { roboticActivityId },
	});

export const useActivityFeedbackView = (param: { roboticActivityId: number | null }) =>
	useQuery({
		queryKey: ['getActivityFeedbackView', param] as const,
		queryFn: () => getActivityFeedbackView(param.roboticActivityId!),
		enabled: param.roboticActivityId !== null,
	});

export const retryRoboticStep = (roboticActivityDetailId: number) =>
	fetcher<void>({
		api: '/robotic-activities/retry',
		body: { roboticActivityDetailId },
	});

export const useRetryRoboticStep = () =>
	useMutation({ mutationFn: retryRoboticStep });

export const updateActivityFeedback = (payload: UpdateFeedbackPayload) =>
	fetcher<void>({
		api: '/robotic-activities/update-feedback',
		body: payload,
	});

export const useUpdateActivityFeedback = () =>
	useMutation({ mutationFn: updateActivityFeedback });

// cancel/return endpoints not confirmed in API contract — flagged for BE
export const cancelAppId = (payload: CancelAppIdPayload) =>
	fetcher<void>({
		api: '/robotic-activities/cancel',
		body: payload,
	});

export const useCancelAppId = () =>
	useMutation({ mutationFn: cancelAppId });

export const returnToQC = (payload: ReturnToQCPayload) =>
	fetcher<void>({
		api: '/robotic-activities/return',
		body: payload,
	});

export const useReturnToQC = () =>
	useMutation({ mutationFn: returnToQC });
